import type { StreamChat } from 'stream-chat';

import type { InboxItem } from '../fixtures/domain.js';
import { RETENTION } from '../fixtures/config.js';
import { useInboxList } from '../data/inbox.js';
import { emptyQuery, withFilter, type InboxQuery } from './query.js';
import type { ChannelId } from '../fixtures/domain.js';
import { Avatar } from '../components/Avatar.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { LoadingState } from '../components/LoadingState.js';
import { PlainMessageText } from '../components/MessageMarkdown.js';
import { DeletionPill } from './DeletionPill.js';
import styles from './EndedPanel.module.css';

/**
 * Ended chats are the ones scheduled for removal: the backend's `closing` and
 * `pending_delete` phases. Marketing threads are excluded — they have their own
 * rail — and `career_fair` never appears, because the `kind` clause carries
 * that exclusion.
 */
const ENDED_QUERY: InboxQuery = withFilter(
  withFilter(emptyQuery(), { kind: 'status', phases: ['closing', 'pending_delete'] }),
  { kind: 'kind', kinds: ['job', 'direct'] },
);

interface EndedPanelProps {
  client: StreamChat | null;
  selectedId: ChannelId | null;
  onSelect: (item: InboxItem) => void;
}

export function EndedPanel({ client, selectedId, onSelect }: EndedPanelProps) {
  const list = useInboxList(ENDED_QUERY, client);
  const items = list.items;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Ended conversations</h1>
        <p className={styles.subtitle}>
          Conversations you've ended. Permanently deleted after {RETENTION.inactivityDays} days, or{' '}
          {RETENTION.endChatWindowMs / 60_000} minutes after your last message is read.
        </p>
      </div>

      <div className={styles.list}>
        {list.loading && items.length === 0 ? (
          <LoadingState label="Fetching conversations" />
        ) : list.error ? (
          <EmptyState
            icon={<Icon name="chatOff" size={32} />}
            title="Couldn't load ended conversations"
            description="Something went wrong reaching the server."
            action={{ label: 'Try again', onClick: list.reload }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon name="chatOff" size={32} />}
            title="No ended conversations"
            description="Conversations you end show up here until they're deleted."
          />
        ) : (
          items.map((item) => (
            <EndedRow
              key={item.channel.id}
              item={item}
              active={item.channel.id === selectedId}
              onClick={() => onSelect(item)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface EndedRowProps {
  item: InboxItem;
  active: boolean;
  onClick: () => void;
}

function EndedRow({ item, active, onClick }: EndedRowProps) {
  const { channel, counterpart } = item;
  const jobTitle = channel.jobMeta?.jobTitle;

  return (
    <button className={active ? styles.rowActive : styles.row} onClick={onClick}>
      <Avatar name={counterpart.name} src={counterpart.avatarUrl} locked={counterpart.locked} />
      <span className={styles.body}>
        <span className={styles.line}>
          <span className={styles.name}>{counterpart.name}</span>
          {/* The deletion state is the whole point of this list, so it gets the
              slot an inbox row gives the timestamp. */}
          <DeletionPill channel={channel} />
        </span>
        {jobTitle && (
          <span className={styles.jobTag}>
            <Icon name="briefcase" size={11} />
            <span className={styles.jobTagText}>{jobTitle}</span>
          </span>
        )}
        <span className={styles.preview}>
          {channel.lastMessageText === '' ? (
            // Ended without anyone ever speaking (AI-2664) — same placeholder
            // the open rows use, for the same reason: a blank line reads as a
            // broken row rather than as an empty conversation.
            <span className={styles.previewEmpty}>No messages yet</span>
          ) : (
            <PlainMessageText text={channel.lastMessageText} />
          )}
        </span>
      </span>
    </button>
  );
}
